import React, { useEffect, useState } from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import TextField from '@material-ui/core/TextField';

import { createReferral } from './actions/create-referral';
import {
  EMPLOYEE_STATUS,
  loadOrgEmployeesByStatus,
} from '../employees/actions/load-org-employees-by-status';
import { loadOrgJobs } from '../job/actions/load-org-jobs';
import { employeesForOrgIdSelector } from '../reducers/employee-selectors';
import { jobsForOrgIdSelector } from '../reducers/job-selectors';

import { UiAutocomplete } from '../ui/ui-autocomplete';
import { UiButton } from '../ui/ui-button';
import { UiDialog } from '../ui/ui-dialog';
import { UiPhoneTextField } from '../ui/ui-phone-text-field';

import { employeeName } from '../employees/utils/employee-name';
import { groupJobsByLocation } from '../util/group-jobs-by-location';
import { jobPhoneNumber } from '../util/job-phone-number';
import { sortAlphabetically, SORT_ORDER } from '../util/sort-functions';
import { isEmail, isPhoneNumber } from '../util/string-validators';

const mapStateToProps = (state: any, { orgId }: InputProps) => ({
  employees: employeesForOrgIdSelector(state, orgId),
  jobs: jobsForOrgIdSelector(state, orgId),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { orgId }: InputProps,
) => ({
  actions: {
    loadEmployees: () => dispatch(loadOrgEmployeesByStatus(orgId, EMPLOYEE_STATUS.CURRENT)),
    loadOrgJobs: () => dispatch(loadOrgJobs(orgId)),
    createReferral: (referral: ReferralCreateRequest) => dispatch(createReferral(referral)),
  },
});

type InputProps = {
  orgId: number;
  onClose: () => void;
};

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps;

export const AddManualReferralDialog = connect(
  mapStateToProps,
  mapDispatchToProps,
)(({ employees, onClose, actions, jobs, orgId }: Props) => {
  const [employeeId, setEmployeeId] = useState<string>('');
  const [jobId, setJobId] = useState<string>('');
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string | null>('');
  const [wantsUsToReachOut, setWantsUsToReachOut] = useState<boolean>(true);
  const [disableReachOut, setDisableReachOut] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    actions.loadEmployees();
    actions.loadOrgJobs();
  });

  useEffect(() => {
    if (email && !isEmail(email)) {
      setEmailError('Please enter a valid email');
    }
    if (!email || isEmail(email)) {
      setEmailError('');
    }
  }, [email]);

  if (!employees || !jobs) {
    return null;
  }

  const employeeOptions = employees
    .filter((e) => e.isCurrentEmployee)
    .sort(sortAlphabetically((e: Employee) => employeeName(e), SORT_ORDER.ASC))
    .map((e) => ({
      label: employeeName(e),
      value: e.employeeId.toString(),
    }));

  const jobsGroupedByLocation = groupJobsByLocation(jobs);
  const locations = Object.keys(jobsGroupedByLocation);
  const unsortedJobOptions = locations
    .map((location) =>
      jobsGroupedByLocation[location].jobs.map((j) => ({
        label: j.title,
        value: j.job_id.toString(),
        groupBy: locations.length > 1 ? location : '',
        fromPhoneNumber: jobPhoneNumber(j),
      })),
    )
    .flat();
  const jobOptions = unsortedJobOptions.sort(
    sortAlphabetically((o: SelectOption<string>) => `${o.groupBy} ${o.label}`, SORT_ORDER.ASC),
  );

  const handleTextFieldChange =
    (setFunction: (value: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setFunction(e.target.value);
    };

  const handleJobSelect = async (value: string) => {
    setJobId(value);

    const job = jobOptions.find((j) => j.value === value);

    if (job && !job.fromPhoneNumber) {
      setWantsUsToReachOut(false);
      setDisableReachOut(true);
    } else {
      setDisableReachOut(false);
    }
  };

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWantsUsToReachOut(e.target.value === 'yes');
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    await actions.createReferral({
      orgId,
      employeeId: parseInt(employeeId, 10),
      jobId: parseInt(jobId, 10),
      firstName,
      lastName,
      email: email || null,
      phoneNumber: phoneNumber || null,
      wantsUsToReachOut,
    });
    setIsLoading(false);
    onClose();
  };

  const isContactFieldsEmpty = !email && !phoneNumber && !firstName && !lastName;
  const isContactInfoValid =
    (!email || (email && isEmail(email))) &&
    (!phoneNumber || (phoneNumber && isPhoneNumber(phoneNumber)));
  const isRequiredFieldsFilled =
    (email || phoneNumber) && firstName && lastName && jobId && employeeId;

  return (
    <UiDialog size="sm" title="Add Referral" onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <UiDialog.Content>
          <div className="typo-subtitle">Who referred this person?</div>
          <UiAutocomplete
            label="Employee *"
            options={employeeOptions}
            value={employeeId}
            onChange={setEmployeeId}
          />
          <div className="typo-subtitle u-margin-top-lg">What job was this person referred for</div>
          <UiAutocomplete
            label="Job *"
            options={jobOptions}
            value={jobId}
            onChange={handleJobSelect}
          />
          <div className="typo-subtitle u-margin-top-lg">
            What are this person&apos;s contact details? (Either email or phone is required)
          </div>
          <div className="u-display-flex">
            <TextField
              required
              className="u-width-100 u-margin-right-sm"
              margin="dense"
              variant="outlined"
              label="First Name"
              value={firstName}
              onChange={handleTextFieldChange(setFirstName)}
            />
            <TextField
              required
              className="u-width-100 u-margin-left-sm"
              margin="dense"
              variant="outlined"
              label="Last Name"
              value={lastName}
              onChange={handleTextFieldChange(setLastName)}
            />
          </div>
          <div className="u-display-flex">
            <TextField
              className="u-width-100 u-margin-right-sm"
              margin="dense"
              variant="outlined"
              label="Email"
              value={email}
              onChange={handleTextFieldChange(setEmail)}
              error={!!emailError}
              helperText={emailError}
            />
            <UiPhoneTextField
              className="u-width-100 u-margin-left-sm"
              label="Phone Number"
              value={phoneNumber}
              onChange={setPhoneNumber}
              variant="outlined"
              margin="dense"
            />
          </div>
          {!isContactFieldsEmpty && !email && !phoneNumber ? (
            <div className="u-color-red">Either email or phone number required</div>
          ) : null}
          <div className="typo-subtitle u-margin-top-xl">
            Do you want us to reach out to this person?
          </div>
          <RadioGroup value={wantsUsToReachOut ? 'yes' : 'no'} onChange={handleRadioChange}>
            <div className="u-display-flex">
              <FormControlLabel
                value="yes"
                control={<Radio />}
                label="Yes"
                disabled={disableReachOut}
              />
              <FormControlLabel
                value="no"
                control={<Radio />}
                label="No"
                disabled={disableReachOut}
              />
            </div>
            {disableReachOut ? (
              <div className="typo-subtitle">
                Job has no associated phone number, so we cannot automatically reach out
              </div>
            ) : null}
          </RadioGroup>
        </UiDialog.Content>
        <UiDialog.Actions>
          <UiButton variant="dismiss" onClick={onClose} disabled={isLoading}>
            Cancel
          </UiButton>
          <UiButton
            type="submit"
            loading={isLoading}
            disabled={!isRequiredFieldsFilled || !isContactInfoValid}
          >
            Add Referral
          </UiButton>
        </UiDialog.Actions>
      </form>
    </UiDialog>
  );
});
